import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

/**
 * 🔔 Error Notification Service
 *
 * Envia notificações de erros críticos por email.
 * Inclui proteção anti-loop para evitar spam de emails.
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorNotificationService {
  private readonly COOLDOWN_MS = 60 * 60 * 1000; // 60 minutos entre notificações do mesmo erro na sessão
  private readonly MAX_ERRORS_PER_SESSION = 5; // Máximo de erros distintos reportados por sessão

  private errorHistory: Map<string, number> = new Map(); // tipo erro => timestamp último envio
  private errorsCountThisSession = 0;
  private sessionStart = Date.now();

  constructor(private http: HttpClient) {
    // Reset contador a cada hora
    setInterval(
      () => {
        this.errorsCountThisSession = 0;
        this.sessionStart = Date.now();
      },
      60 * 60 * 1000,
    );
  }

  /**
   * Notifica um erro crítico por email
   * @param error Objeto de erro
   * @param context Contexto adicional (componente, ação, etc)
   */
  notifyCriticalError(
    error: any,
    context?: {
      component?: string;
      action?: string;
      userNIF?: string;
      url?: string;
      [key: string]: any;
    },
  ): void {
    // 🛡️ PROTEÇÃO ANTI-LOOP
    if (!this.shouldSendNotification(error, context)) {
      console.warn('🔕 Notificação de erro bloqueada (proteção anti-loop)', {
        reason: this.getBlockReason(error, context),
        error: error,
      });
      return;
    }

    // Extração segura da mensagem para nunca gerar [object Object]
    let safeMessage = 'Erro desconhecido';
    if (typeof error === 'string' && error.trim()) {
      safeMessage = error.trim();
    } else if (error?.message && typeof error.message === 'string' && error.message.trim()) {
      safeMessage = error.message.trim();
    } else if (error?.toString && typeof error.toString === 'function' && error.toString() !== '[object Object]') {
      safeMessage = error.toString();
    } else if (error && typeof error === 'object') {
      try {
        safeMessage = JSON.stringify(error);
      } catch (_) {
        safeMessage = 'Erro de objeto não serializável';
      }
    }

    // Extração segura de stack trace (com fallback de new Error para nunca ficar vazio nem [object Object])
    let safeStack = '';
    if (typeof error?.stack === 'string' && error.stack.trim() && error.stack !== '[object Object]') {
      safeStack = error.stack;
    } else if (typeof context?.stack === 'string' && context.stack.trim() && context.stack !== '[object Object]') {
      safeStack = context.stack;
    } else {
      safeStack = new Error(safeMessage).stack || 'Sem stack trace disponível';
    }

    const rawStatus = Number(error?.status ?? error?.statusCode ?? context?.status ?? 0);

    // Preparar dados do erro
    const errorData = {
      timestamp: new Date().toISOString(),
      environment: environment.production ? 'production' : 'development',
      appVersion: environment.appVersion,

      // Informações do erro
      message: safeMessage,
      stack: safeStack,
      name: error?.name || (typeof error === 'string' ? 'StringRejection' : 'FrontendError'),
      status: rawStatus || undefined,

      // Contexto
      context: context || {},

      // Informações do browser
      browser: {
        userAgent: navigator.userAgent,
        url: window.location.href,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        timestamp: Date.now(),
      },

      // Informações da sessão
      session: {
        errorCount: this.errorsCountThisSession,
        sessionDuration: Date.now() - this.sessionStart,
      },
    };

    // Registar este erro
    this.registerError(error, context);

    // Enviar notificação via API
    this.sendNotification(errorData);
  }

  /**
   * Verifica se deve enviar notificação (proteção anti-loop)
   */
  private shouldSendNotification(error: any, context?: any): boolean {
    // 0. Ignorar erros de autorização/rede e teardown (401, 403, Forbidden, Unauthorized, Offline, Erros Genéricos HTTP)
    const rawMsg = String(error?.message || (typeof error === 'string' ? error : '') || '').toLowerCase();
    const rawStatus = Number(error?.status ?? error?.statusCode ?? context?.status ?? 0);
    if (
      rawStatus === 401 ||
      rawStatus === 403 ||
      rawMsg === 'forbidden' ||
      rawMsg.includes('forbidden') ||
      rawMsg === 'unauthorized' ||
      rawMsg.includes('unauthorized') ||
      rawMsg.includes('errors.forbidden') ||
      rawMsg.includes('errors.unauthorized') ||
      rawMsg.includes('errors.unexpected') ||
      rawMsg.includes('errors.no_internet') ||
      rawMsg.includes('errors.timeout') ||
      rawMsg.includes('errors.not_found') ||
      rawMsg.includes('errors.bad_request') ||
      rawMsg.includes('errors.offline') ||
      rawMsg.includes('unexpected error') ||
      rawMsg.includes('não foi possível concluir a operação') ||
      rawMsg.includes('se produjo un error inesperado') ||
      rawMsg.includes('une erreur inattendue') ||
      rawMsg.includes('si è verificato un errore') ||
      rawMsg.includes('object unsubscribed') ||
      rawMsg.includes('objectunsubscribederror') ||
      rawMsg.includes('loading chunk') ||
      rawMsg.includes('chunkloaderror') ||
      rawMsg.includes('net::err_aborted') ||
      rawMsg.includes('failed to fetch') ||
      rawMsg.includes('networkerror')
    ) {
      return false;
    }

    // 0.1. Nunca enviar em ambiente local (localhost / 127.0.0.1) ou sob headless/test automation (Playwright, HeadlessChrome, Prerender)
    if (typeof window !== 'undefined') {
      const hostname = window.location?.hostname || '';
      if (
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname === '0.0.0.0' ||
        hostname.endsWith('.local')
      ) {
        return false;
      }
      const ua = navigator?.userAgent || '';
      if (ua.includes('HeadlessChrome') || ua.includes('Playwright') || ua.includes('Prerender')) {
        return false;
      }
    }

    // 1. Não enviar em desenvolvimento (a menos que seja forçado)
    if (!environment.production && !context?.forceNotify) {
      return false;
    }

    // 2. Limite de erros por sessão
    if (this.errorsCountThisSession >= this.MAX_ERRORS_PER_SESSION) {
      return false;
    }

    // 3. Cooldown para mesmo tipo de erro
    const errorKey = this.getErrorKey(error, context);
    const lastSent = this.errorHistory.get(errorKey);

    if (lastSent && Date.now() - lastSent < this.COOLDOWN_MS) {
      return false;
    }

    return true;
  }

  /**
   * Obtém chave única para o erro (para cooldown)
   */
  private getErrorKey(error: any, context?: any): string {
    const message = error?.message || error?.toString() || 'unknown';
    const component = context?.component || 'global';
    const action = context?.action || 'unknown';

    return `${component}:${action}:${message.substring(0, 50)}`;
  }

  /**
   * Obtém razão pela qual o erro foi bloqueado
   */
  private getBlockReason(error: any, context?: any): string {
    const rawMsg = String(error?.message || (typeof error === 'string' ? error : '') || '').toLowerCase();
    const rawStatus = Number(error?.status ?? error?.statusCode ?? context?.status ?? 0);
    if (
      rawStatus === 401 ||
      rawStatus === 403 ||
      rawMsg === 'forbidden' ||
      rawMsg.includes('forbidden') ||
      rawMsg === 'unauthorized' ||
      rawMsg.includes('unauthorized')
    ) {
      return 'Erro de autorização (401/403/Forbidden suprimido)';
    }
    if (
      rawMsg.includes('errors.unexpected') ||
      rawMsg.includes('errors.no_internet') ||
      rawMsg.includes('errors.timeout') ||
      rawMsg.includes('unexpected error') ||
      rawMsg.includes('não foi possível concluir a operação') ||
      rawMsg.includes('net::err_aborted')
    ) {
      return 'Erro HTTP/rede genérico suprimido';
    }
    if (typeof window !== 'undefined') {
      const hostname = window.location?.hostname || '';
      if (
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname === '0.0.0.0' ||
        hostname.endsWith('.local')
      ) {
        return 'Ambiente local (localhost bloqueado)';
      }
      const ua = navigator?.userAgent || '';
      if (ua.includes('HeadlessChrome') || ua.includes('Playwright') || ua.includes('Prerender')) {
        return 'Ambiente headless / pre-render / automação bloqueado';
      }
    }
    if (!environment.production && !context?.forceNotify) {
      return 'Ambiente de desenvolvimento';
    }

    if (this.errorsCountThisSession >= this.MAX_ERRORS_PER_SESSION) {
      return `Limite de erros atingido (${this.MAX_ERRORS_PER_SESSION} por sessão)`;
    }

    const errorKey = this.getErrorKey(error, context);
    const lastSent = this.errorHistory.get(errorKey);

    if (lastSent) {
      const timeSince = Date.now() - lastSent;
      const cooldownRemaining = this.COOLDOWN_MS - timeSince;
      return `Cooldown ativo (${Math.ceil(cooldownRemaining / 1000 / 60)} minutos restantes)`;
    }

    return 'Motivo desconhecido';
  }

  /**
   * Registra o erro no histórico
   */
  private registerError(error: any, context?: any): void {
    const errorKey = this.getErrorKey(error, context);
    this.errorHistory.set(errorKey, Date.now());
    this.errorsCountThisSession++;

    // Limpar histórico antigo (> 1 hora)
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    for (const [key, timestamp] of this.errorHistory.entries()) {
      if (timestamp < oneHourAgo) {
        this.errorHistory.delete(key);
      }
    }
  }

  /**
   * Envia a notificação via API
   */
  private sendNotification(errorData: any): void {
    // Endpoint da API para enviar notificações de erro
    const endpoint = `${environment.URL}/common/NotifyFrontendError`;

    this.http.post(endpoint, errorData).subscribe({
      next: () => {
        console.log('✅ Notificação de erro enviada com sucesso');
      },
      error: () => {
        // Não logar erro aqui para evitar loop infinito
        // Apenas silenciosamente falhar
        console.warn('⚠️ Falha ao enviar notificação de erro (ignorado)');
      },
    });
  }

  /**
   * Obtém estatísticas de erros da sessão
   */
  getSessionStats(): { count: number; duration: number; historySize: number } {
    return {
      count: this.errorsCountThisSession,
      duration: Date.now() - this.sessionStart,
      historySize: this.errorHistory.size,
    };
  }

  /**
   * Limpa o histórico de erros (útil para testes)
   */
  clearHistory(): void {
    this.errorHistory.clear();
    this.errorsCountThisSession = 0;
    this.sessionStart = Date.now();
  }
}
